import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Indicator,
  Stack,
  Tabs,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PiArchive,
  PiArrowCounterClockwise,
  PiCheck,
  PiCopy,
  PiCopySimple,
  PiEnvelope,
  PiFilePlus,
  PiLink,
  PiLockOpen,
} from 'react-icons/pi';
import { Link, Redirect, useLocation, useParams } from 'wouter';
import { unlockOffered } from '@/components/InvoiceDetailsModal/invoiceActions';
import { refusal } from '@/components/InvoicesPage/CancelInvoiceModal';
import Loader from '@/components/Loader';
import { StatusHeaderBar } from '@/components/StatusHeaderBar/StatusHeaderBar';
import { TestBadge } from '@/components/TestBadge';
import { useConfirmation } from '@/hooks/useConfirmation';
import { useInvoiceDetailsByOfferId } from '@/models/invoices';
import {
  getOfferStatus,
  offerStatusColor,
  offerStatusLabel,
  TOffer,
  useArchiveOffer,
  useIssueNewProforma,
  useOffer,
  useReSendOffer,
  useRevertOffer,
  useSendOffer,
  useUpdateOffer,
} from '@/models/offers';
import { useOfferTabs } from '@/models/permissions';
import { useSplitPlan } from '@/models/split-plan';
import DuplicateOfferModal from './DuplicateOfferModal';
import classes from './OfferEditorPage.module.css';
import DeliveryTab from './tabs/DeliveryTab';
import FinanceTab from './tabs/FinanceTab';
import GeneralTab from './tabs/GeneralTab';
import LegalTab from './tabs/LegalTab';

const payerKycLabel = (kycExpiresAt: string | null) => {
  if (!kycExpiresAt) return 'KYC missing';
  return new Date(kycExpiresAt) > new Date()
    ? `KYC valid till ${dayjs(kycExpiresAt).format('DD.MM.YYYY')}`
    : `KYC expired ${dayjs(kycExpiresAt).format('DD.MM.YYYY')}`;
};

/**
 * An attached party is not the same as a cleared one. Stating the KYC date as a
 * bare fact reads as "the payer is sorted", so an SR promises the customer a
 * step that has not been cleared. Missing and expired both still require the
 * customer to verify -- bound to this same party, renewing it in place.
 */
const payerNeedsKyc = (kycExpiresAt: string | null) =>
  !kycExpiresAt || new Date(kycExpiresAt) <= new Date();

export const CUSTOMER_PORTAL_URL =
  import.meta.env.VITE_CUSTOMER_PORTAL_URL || 'https://portal.legalbison.com';

export default function OfferHubPage() {
  const { id, tab } = useParams();
  const offerId = Number(id);
  const [, navigate] = useLocation();
  const { data: offer, isLoading, isError } = useOffer(offerId);
  const { data: planData } = useSplitPlan(offerId);
  const { tabs, policy, isLoading: permsLoading } = useOfferTabs();

  const [dupModalOpen, setDupModalOpen] = useState(false);
  const archiveOffer = useArchiveOffer();

  if (isLoading || permsLoading) return <Loader />;
  if (isError || !offer) {
    return (
      <Text c="red">
        Unable to load offer. It may not exist or you don't have permission.
      </Text>
    );
  }

  // A link may carry no tab, an unknown one, or one this viewer may not see. All three
  // resolve to the first tab they are entitled to, so an offer link works for anyone.
  if (!tab || !tabs.some((t) => t.key === tab)) {
    return <Redirect to={`/${offerId}/${tabs[0].key}`} replace />;
  }

  const { status, detail } = getOfferStatus(offer);
  const isAccepted = offer.accepted;
  const offerUrl = `${CUSTOMER_PORTAL_URL}/offer/${offer.uuid}`;
  const planStatus = planData?.plan?.status ?? 'none';
  const awaitingBizDev = planStatus === 'proposed';

  return (
    <Stack gap="md">
      <DuplicateOfferModal
        offerId={offer.id}
        offerTitle={offer.title}
        opened={dupModalOpen}
        onClose={() => setDupModalOpen(false)}
      />

      {/* Header */}
      <StatusHeaderBar
        statusLabel={offerStatusLabel[status]}
        color={offerStatusColor[status]}
        underLayer={
          offer.archivedAt
            ? { label: 'Archived', icon: <PiArchive size={40} /> }
            : undefined
        }
        description={detail}
        title={
          <Group gap="xs" wrap="nowrap">
            <OfferTitleEditor offer={offer} disabled={isAccepted} />
            {offer.isTest && <TestBadge size="md" />}
          </Group>
        }
        subtitles={
          <>
            <Group gap={6} mt={4} wrap="nowrap">
              <Text span fz="sm" fw={500} c="dimmed">
                Offer sends to:
              </Text>
              <Anchor href={`mailto:${offer.contactEmail}`} fz="sm" fw={500}>
                {offer.contactEmail}
              </Anchor>
              <CopyButton value={offer.contactEmail}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy address'}>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color={copied ? 'teal' : 'gray'}
                      onClick={copy}
                    >
                      {copied ? <PiCheck size={12} /> : <PiCopy size={12} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
              {offer.contactName && (
                <Text span fz="sm" c="dimmed">
                  ({offer.contactName})
                </Text>
              )}
            </Group>
            {offer.currentContactEmail &&
              offer.currentContactEmail !== offer.contactEmail && (
                <Text fz="xs" c="orange.7">
                  The contact record now reads {offer.currentContactEmail}. This
                  offer keeps sending to the address captured when it was
                  created.
                </Text>
              )}
            {offer.accountName && (
              <Text fz="sm" c="dimmed">
                <Text span fz="sm" fw={500} c="dimmed">
                  Account:
                </Text>{' '}
                {offer.accountName}
              </Text>
            )}
            {offer.payer && (
              <Text fz="sm" c="dimmed">
                <Text span fz="sm" fw={500} c="dimmed">
                  Payer:
                </Text>{' '}
                <Anchor
                  component={Link}
                  to={`~/payers/${offer.payer.id}`}
                  fz="sm"
                >
                  {offer.payer.displayName}
                </Anchor>
                , {offer.payer.clientType},{' '}
                {payerKycLabel(offer.payer.kycExpiresAt)}
                {offer.payer.deletedAt ? (
                  <Tooltip label="The attached payer was deleted and cannot be used — replace or detach it on the Finance tab">
                    <Badge ml={8} size="xs" color="red" variant="filled">
                      Payer unusable
                    </Badge>
                  </Tooltip>
                ) : (
                  payerNeedsKyc(offer.payer.kycExpiresAt) && (
                    <Tooltip
                      multiline
                      w={300}
                      label="Attaching a payer does not clear verification. The customer will still have to pass KYC before the order can complete — it starts from this party's details and renews it in place."
                    >
                      <Badge ml={8} size="xs" color="orange" variant="filled">
                        Customer must still pass KYC
                      </Badge>
                    </Tooltip>
                  )
                )}
              </Text>
            )}
            <Text fz="sm" c="dimmed">
              <Text span fz="sm" fw={500} c="dimmed">
                Payment terms:
              </Text>{' '}
              {planData?.splitEnabled ? 'Split Payment' : 'Single Invoice'}
            </Text>
            {offer.kycStatus && offer.kycStatus !== 'pending' && (
              <Group gap="xs" mt={6}>
                <KycStatusBadge status={offer.kycStatus} />
              </Group>
            )}
          </>
        }
        actions={
          <>
            {policy.canViewCommercial && (
              <>
                <OfferActions offer={offer} />
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<PiCopySimple size={14} />}
                  onClick={() => setDupModalOpen(true)}
                >
                  Duplicate
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  color="gray"
                  leftSection={
                    offer.archivedAt ? (
                      <PiArrowCounterClockwise size={14} />
                    ) : (
                      <PiArchive size={14} />
                    )
                  }
                  loading={archiveOffer.isPending}
                  onClick={() =>
                    archiveOffer.mutate({
                      offerId: offer.id,
                      archived: !offer.archivedAt,
                    })
                  }
                >
                  {offer.archivedAt ? 'Unarchive' : 'Archive'}
                </Button>
                <CopyButton value={offerUrl}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy offer link'}>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={
                          copied ? <PiCopy size={14} /> : <PiLink size={14} />
                        }
                        onClick={copy}
                        color={copied ? 'teal' : undefined}
                      >
                        {copied ? 'Copied' : 'Copy Link'}
                      </Button>
                    </Tooltip>
                  )}
                </CopyButton>
              </>
            )}
          </>
        }
      />

      {offer.accepted && offer.proformaState?.newProforma && (
        <NoActiveProformaNotice offer={offer} />
      )}

      {/* Tabs */}
      <Tabs value={tab} onChange={(v) => v && navigate(`/${offerId}/${v}`)}>
        <Tabs.List>
          {tabs.map(({ key, label }) => (
            <Tabs.Tab key={key} value={key}>
              {key === 'finance' ? (
                <Indicator
                  disabled={!awaitingBizDev}
                  color="blue"
                  size={7}
                  offset={-4}
                >
                  <span>{label}</span>
                </Indicator>
              ) : (
                label
              )}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Box pt="md">
          {tab === 'general' && <GeneralTab offer={offer} />}
          {tab === 'legal' && <LegalTab offer={offer} />}
          {tab === 'finance' && (
            <FinanceTab offer={offer} restricted={!policy.canViewFullFinance} />
          )}
          {tab === 'delivery' && <DeliveryTab offer={offer} />}
        </Box>
      </Tabs>
    </Stack>
  );
}

const kycBadgeProps: Record<string, { color: string; label: string }> = {
  in_progress: { color: 'yellow', label: 'KYC in progress' },
  approved: { color: 'green', label: 'KYC approved' },
  rejected: { color: 'red', label: 'KYC rejected' },
  retry: { color: 'orange', label: 'KYC retry' },
};

export function KycStatusBadge({ status }: { status: string }) {
  const props = kycBadgeProps[status];
  if (!props) return null;
  return (
    <Badge size="lg" variant="light" color={props.color}>
      {props.label}
    </Badge>
  );
}

function OfferActions({ offer }: { offer: TOffer }) {
  const sendOffer = useSendOffer();
  const reSendOffer = useReSendOffer();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const noAddress = offer.contactEmail?.trim()
    ? null
    : 'This offer has no contact email address, so it cannot be sent';

  const handleSend = async () => {
    const ok = await confirm({
      title: 'Send Offer',
      message: (
        <>
          Send this offer to <b>{offer.contactEmail}</b>?
        </>
      ),
      confirmButtonText: 'Send',
    });
    if (!ok) return;
    try {
      await sendOffer.mutateAsync({ offerId: offer.id });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `Failed to send: ${e}`,
      });
    }
  };

  const handleReSend = async () => {
    const ok = await confirm({
      title: 'Re-send Offer',
      message: (
        <>
          Re-send this offer to <b>{offer.contactEmail}</b>?
        </>
      ),
      confirmButtonText: 'Re-send',
    });
    if (!ok) return;
    try {
      await reSendOffer.mutateAsync({ offerId: offer.id });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `Failed to re-send: ${e}`,
      });
    }
  };

  return (
    <>
      <ConfirmationDialog />
      {!offer.accepted && !offer.sentAt && (
        <Tooltip label={noAddress} disabled={!noAddress}>
          <Button
            size="xs"
            leftSection={<PiEnvelope size={14} />}
            onClick={handleSend}
            loading={sendOffer.isPending}
            disabled={Boolean(noAddress)}
          >
            Send
          </Button>
        </Tooltip>
      )}
      {!offer.accepted && offer.sentAt && (
        <Tooltip label={noAddress} disabled={!noAddress}>
          <Button
            size="xs"
            variant="light"
            leftSection={<PiEnvelope size={14} />}
            onClick={handleReSend}
            loading={reSendOffer.isPending}
            disabled={Boolean(noAddress)}
          >
            Re-send
          </Button>
        </Tooltip>
      )}
      {offer.accepted && <IssueNewProformaButton offer={offer} />}
      {offer.accepted && <RevertButton offer={offer} />}
    </>
  );
}

function OfferTitleEditor({
  offer,
  disabled,
}: {
  offer: TOffer;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(offer.title);
  const update = useUpdateOffer();

  const save = async () => {
    if (title.trim() && title !== offer.title) {
      await update.mutateAsync({ offerId: offer.id, title: title.trim() });
    }
    setEditing(false);
  };

  if (disabled || !editing) {
    return (
      <Text
        fw={600}
        fz="xl"
        onClick={() => !disabled && setEditing(true)}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
      >
        {offer.title}
      </Text>
    );
  }

  return (
    <TextInput
      value={title}
      onChange={(e) => setTitle(e.currentTarget.value)}
      onBlur={save}
      onKeyDown={(e) => e.key === 'Enter' && save()}
      autoFocus
      size="md"
      fw={600}
    />
  );
}

const gapLabel = (offer: TOffer) => {
  const seq = offer.proformaState?.newProforma?.seq;
  return seq == null ? 'The proforma' : `Installment ${seq}'s proforma`;
};

function NoActiveProformaNotice({ offer }: { offer: TOffer }) {
  return (
    <Alert color="orange" title="Accepted - no active proforma">
      <Text fz="sm">
        {gapLabel(offer)} was cancelled by accounting and the client has nothing
        to pay against. The offer stays accepted.{' '}
        {offer.mayUnlock
          ? "Issue a new proforma (accepted positions and selection, today's exchange rate), Unlock the offer so the client accepts again, or Archive it."
          : "The offer's owner decides what follows: a new proforma, Unlock, or Archive."}
      </Text>
    </Alert>
  );
}

export function IssueNewProformaButton({ offer }: { offer: TOffer }) {
  const issue = useIssueNewProforma();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const requestKey = useRef<string | null>(null);
  const slot = offer.proformaState?.newProforma;
  if (!slot || !offer.mayUnlock) return null;
  const archived = !!offer.archivedAt;

  const handleClick = async () => {
    const ok = await confirm({
      title: 'Issue new proforma',
      message: `A new proforma with a new number is issued ${
        slot.seq == null ? 'for this order' : `for installment ${slot.seq}`
      } on the accepted positions, selection, prices and discount, at today's exchange rate, and emailed to the paying party. The client does not accept again. The cancelled proforma stays as it is.`,
      confirmButtonText: 'Issue new proforma',
    });
    if (!ok) return;
    requestKey.current ??= crypto.randomUUID();
    try {
      const res = await issue.mutateAsync({
        offerId: offer.id,
        requestKey: requestKey.current,
      });
      requestKey.current = null;
      notifications.show({
        color: 'teal',
        title: `Proforma ${res.invoiceNumber} issued`,
        message: 'It was emailed to the paying party.',
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'No new proforma issued',
        message: refusal(e) ?? String(e),
      });
    }
  };

  return (
    <>
      <ConfirmationDialog />
      <Tooltip
        label="The offer is archived. Unarchive it to issue a new proforma"
        disabled={!archived}
      >
        <Button
          size="xs"
          leftSection={<PiFilePlus size={14} />}
          onClick={handleClick}
          loading={issue.isPending}
          disabled={archived}
        >
          Issue new proforma
        </Button>
      </Tooltip>
    </>
  );
}

function RevertButton({ offer }: { offer: TOffer }) {
  const { data: invoices, isSuccess } = useInvoiceDetailsByOfferId(offer.id);
  const live = (invoices ?? []).filter((i) => i.status !== 'cancelled');
  const revert = useRevertOffer();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const [phase, setPhase] = useState<'idle' | 'countdown'>('idle');
  const [seconds, setSeconds] = useState(10);
  const timerRef = useRef<number>(undefined);
  const intervalRef = useRef<number>(undefined);

  const canRevert = isSuccess && unlockOffered(offer, invoices ?? []);
  const clientReportedPayment = live.some(
    (i) => (i.activeReportCount ?? 0) > 0,
  );
  const liveNumbers = live.map((i) => `#${i.invoiceNumber}`).join(', ');

  const cancelCountdown = useCallback(() => {
    clearTimeout(timerRef.current);
    clearInterval(intervalRef.current);
    setPhase('idle');
    setSeconds(10);
  }, []);

  const startCountdown = useCallback(() => {
    setPhase('countdown');
    setSeconds(10);
    intervalRef.current = window.setInterval(
      () => setSeconds((s) => s - 1),
      1000,
    );
    timerRef.current = window.setTimeout(async () => {
      clearInterval(intervalRef.current);
      setPhase('idle');
      try {
        await revert.mutateAsync({ offerId: offer.id });
      } catch (e) {
        notifications.show({
          color: 'red',
          title: 'Error',
          message: `Failed to revert: ${e}`,
        });
      }
    }, 10000);
  }, [offer.id, revert]);

  const handleClick = useCallback(async () => {
    const ok = await confirm({
      title: 'Unlock Offer',
      message: `${
        liveNumbers
          ? `This will cancel invoice ${liveNumbers} and unlock the offer for editing.`
          : 'Every invoice of this offer is already cancelled. This will unlock the offer for editing.'
      } This action cannot be undone.${
        clientReportedPayment
          ? ' A payment confirmation has already been provided for this invoice.'
          : ''
      }`,
      confirmButtonText: 'Unlock',
    });
    if (ok) startCountdown();
  }, [liveNumbers, clientReportedPayment, startCountdown, confirm]);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(intervalRef.current);
    };
  }, []);

  if (!canRevert) return null;

  if (phase === 'countdown') {
    return (
      <Button
        size="xs"
        color="gray"
        variant="light"
        onClick={cancelCountdown}
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        <Box className={classes.countdownOverlay} />
        Undo ({seconds}s)
      </Button>
    );
  }

  return (
    <>
      <ConfirmationDialog />
      <Button
        size="xs"
        color="red"
        variant="light"
        leftSection={<PiLockOpen size={14} />}
        onClick={handleClick}
        loading={revert.isPending}
      >
        Unlock Offer
      </Button>
    </>
  );
}
